import type { Session, User } from '@supabase/supabase-js';

/**
 * supabase-js returns a freshly parsed User/Session object on every
 * getSession() and every auth event, even when nothing changed. Putting each
 * one into React state gave every consumer a "new" user several times per
 * refocus, and anything keyed on the object (SubscriptionProvider's
 * get-subscription-status effect among them) refetched each time. Keep the
 * previous object when the content is identical.
 */
export function sameUser(prev: User | null, next: User | null): boolean {
  if (prev === next) return true;
  if (!prev || !next || prev.id !== next.id) return false;
  try {
    return JSON.stringify(prev) === JSON.stringify(next);
  } catch {
    return false;
  }
}

export function sameSession(prev: Session | null, next: Session | null): boolean {
  if (prev === next) return true;
  if (!prev || !next) return false;
  return (
    prev.access_token === next.access_token &&
    prev.refresh_token === next.refresh_token &&
    prev.expires_at === next.expires_at &&
    sameUser(prev.user, next.user)
  );
}

/** For setState updaters: keep `prev` when `next` carries the same content. */
export const keepUserIfSame = (next: User | null) => (prev: User | null) => (sameUser(prev, next) ? prev : next);
export const keepSessionIfSame = (next: Session | null) => (prev: Session | null) => (sameSession(prev, next) ? prev : next);
